/**
 * Copyright (C) 2018–2020 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiInputBase } from '@here/lui-base'
import { LuiHighlightableMixin } from '@here/lui-base/lui-higlightable-mixin'
import template from 'lui-menuitem.html'
import scopedCss from 'lui-menuitem.scss'

export default class LuiMenuitem extends LuiHighlightableMixin(LuiInputBase) {
  static get is() {
    return 'lui-menuitem'
  }

  static get clickTriggers() {
    return ['enter', 'space']
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  constructor() {
    super()
    this._autoSelect = false
  }

  init() {
    super.init()
    this._clickBound = this._clickBound || this._clickAction.bind(this)
    this._mouseenterBound = this._mouseenterBound || this._mouseenterAction.bind(this)
    this.field.addEventListener('click', this._clickBound)
    this.field.addEventListener('mouseenter', this._mouseenterBound)
  }

  highlightChanged(oldValue, newValue) {
    this.highlight(newValue)
  }

  onDisconnected() {
    super.onDisconnected()
    this.field.removeEventListener('click', this._clickBound)
    this.field.removeEventListener('mouseenter', this._mouseenterBound)
  }

  iconChanged(oldValue, newValue) {
    let element = this.shadowRoot.querySelector('lui-icon')
    if (newValue) {
      if (!element) {
        element = document.createElement('lui-icon')
      }
      element.setAttribute('icon', newValue)
      const span = this.shadowRoot.querySelector('span')
      span.parentNode.insertBefore(element, span)
    } else if (element) {
      this.seedElement.removeChild(element)
    }
  }

  _mouseenterAction() {
    this.field.focus()
  }

  _isLuiMenuDescendant(element) {
    return element.closest('lui-menu') !== null
  }

  _clickAction(event) {
    if (this.disabled) return
    if (this.selected) return
    this.selected = true
    if (this._isLuiMenuDescendant(event.target)) {
      const menu = this.closest('lui-menu')
      menu.open = !menu.open
    }
  }

  _getField() {
    this._field = this.closest('a, button')
    if (this._field) {
      this.removeAttribute('tabindex')
      this._field.classList.add('lui-reset-styles')
    }
    return this._field || this
  }

  get selected() {
    return this.hasAttribute('selected')
  }

  set selected(value) {
    if (value) {
      if (this._autoSelect) this.setAttribute('selected', '')
      this.dispatchEvent(new Event('select', {bubbles: true}))
    } else {
      if (this._autoSelect) this.removeAttribute('selected')
    }
  }

  get autoSelect() {
    return this._autoSelect
  }

  set autoSelect(value) {
    this._autoSelect = value
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-menu/lui-menuitem.js
// module id = 246
// module chunks = 0 1 3